import { PropsWithChildren, ReactNode, useCallback } from 'react';
import styles from './CasAccordion.module.scss';
import { VdsIcon } from '@volt/volt-extended-components-react';
import useCollapsibleAccordion from '@customer/components/accordions/useCollapsibleAccordion';
import { ControlledAccordion } from '@customer/components/accordions/useControlledAccordions';
import { AccordionStatus } from '@customer/components/accordions/types';
import type { IconColors, Colors } from '@volt/volt-basic-components/dist/types/definitions/colors';

const getIconStatusColor = (status: AccordionStatus): IconColors | Colors => {
  switch (status) {
    case AccordionStatus.Healthy:
      return 'green-500';
    case AccordionStatus.Caution:
      return 'yellow-500';
    case AccordionStatus.Warning:
      return 'orange-500';
    case AccordionStatus.Danger:
      return 'red-500';
    case AccordionStatus.Informative:
      return 'color-icon-brand-default';
    case AccordionStatus.Neutral:
    default:
      return 'color-icon-medium';
  }
};

export interface CasAccordionProps {
  status: AccordionStatus;
  header: ReactNode;
  icon?: string;
  defaultExpanded?: boolean;
  controlled?: ControlledAccordion;
  hideControl?: boolean;
}

export function CasAccordion({
  status,
  icon,
  header,
  children,
  defaultExpanded,
  controlled,
  hideControl = false,
}: PropsWithChildren<CasAccordionProps>) {
  const { isExpanded, changeExpanded } = useCollapsibleAccordion({
    defaultExpanded,
    controlled,
  });

  const onHeaderClick = useCallback(() => {
    if (!hideControl) {
      changeExpanded();
    }
  }, [changeExpanded, hideControl]);

  return (
    <section className={styles.accordion}>
      <header
        className={`${styles.header} ${!icon ? styles['header--no-icon'] : ''} ${!hideControl ? styles['header--clickable'] : ''}`}
        onClick={onHeaderClick}
      >
        {icon && <VdsIcon svgIcon={icon} size="32px" color={getIconStatusColor(status)} />}
        {header}
        {!hideControl && (
          <VdsIcon
            svgIcon={isExpanded ? 'chevron-up' : 'chevron-down'}
            size="28px"
            color="color-icon-medium"
          />
        )}
      </header>
      <div className={`${styles.body} ${isExpanded ? '' : styles['body--collapsed']}`}>
        <div className={styles.children}>{children}</div>
      </div>
    </section>
  );
}

export default CasAccordion;
